import type { ReactNode } from 'react';
import type { PortalDog, PortalInfo, PortalStay, StayPhase } from '@bbbk/schema';
import { Avatar } from '../components/common';
import { PendingButton } from '../components/common/PendingButton';
import { G } from './glyphs';
import { Tag } from './Tag';
import { cardDoor, cardSay, dogsOn, moneyRows, nightsLabel, opener, stageStrip } from './homeRules';
import { WhatsAppMark } from './MessagePatricia';
import { dogNames, longDay } from './requestRules';
import { whatsappHref, WHATSAPP_HREF } from './whatsapp';
import { payPath } from './landing';

// G20 · the stay card, G21 · the money list, G19 · the stage strip. One card for
// every Home state; the PHASE decides the words, the strip, the tag and the
// one door at the foot (`cardDoor`). After `confirmed` there never is a stage
// left; after collection there is at most a balance. THE CARD IS NOT A LINK
// (Freddi, 2026-10-09; deviation 15): Stays is one level, every stay is on it,
// reached by a swipe or the counter — so nothing on the card is an arrow, and
// the door at its foot is the only thing on it that goes anywhere.

const STAGES: { key: string; word: string; icon: (p: { size: number }) => ReactNode }[] = [
  { key: 'req', word: 'Requested', icon: G.clipboardCheck },
  { key: 'acc', word: 'Accepted', icon: G.thumbsUp },
  { key: 'dep', word: 'Deposit paid', icon: G.euro },
  { key: 'con', word: 'Booked', icon: G.calendarCheck },
];

const SAY_ICON = { clock: G.clock, euro: G.euro, tick: G.tick };

export interface StayCardProps {
  stay: PortalStay;
  phase: StayPhase;
  dogs: readonly PortalDog[];
  info: PortalInfo | null;
  contactName: string;
  /** Where a money door goes (deposit-proof AC1): the pay sheet, by path. */
  onGo?: (path: string) => void;
}

function Who({ stay, dogs, tag, children }: { stay: PortalStay; dogs: readonly PortalDog[]; tag: 'Arrived' | 'Collected' | null; children?: ReactNode }) {
  const on = dogsOn(stay, dogs);
  const inner = (
    <>
      <span className="cs-avs">
        {on.dogs.map((d) => (
          <Avatar key={d.id} kind="dog" name={d.name} id={d.id} size={34} initials={(d.name.trim()[0] ?? '?').toUpperCase()} />
        ))}
      </span>
      <div>
        <b>{dogNames(on.names)}</b>
        {children}
      </div>
      {/* THE STATUS PILL, top right, IN THE ROW (Freddi, 2026-10-09): a flex item
          after the names, so the card keeps one structure in every phase and
          the names take whatever room is left rather than a reserved strip. */}
      {tag && (
        <span className="hs-tag hs-tag-tr">
          <Tag tone={tag === 'Arrived' ? 'ok' : 'neutral'}>{tag}</Tag>
        </span>
      )}
    </>
  );
  return <div className="hs-who">{inner}</div>;
}

export function StayCard({ stay, phase, dogs, info, contactName, onGo }: StayCardProps) {
  const on = dogsOn(stay, dogs);
  const breeds = on.dogs.map((d) => d.breed).filter((b): b is string => !!b).join(' · ');
  const nights = nightsLabel(stay.checkIn, stay.checkOut);

  const state = phase === 'requested' ? 'requested' : phase === 'before' ? (stay.status === 'unconfirmed' ? 'accepted' : 'confirmed') : phase === 'after' ? 'staying st-collected' : 'staying';
  const tag = phase === 'staying' || phase === 'collection' ? 'Arrived' : phase === 'after' ? 'Collected' : null;
  const say = cardSay(stay, phase, info);
  const door = cardDoor(stay, phase);
  const rows = moneyRows(stay, phase);

  return (
    <div className={`cp-card stay st-${state}`}>
      <Who stay={stay} dogs={dogs} tag={tag}>
        <small>{breeds}</small>
      </Who>
      <div className="hs-when">
        <span>
          {G.sun({ size: 13 })}
          <b>{longDay(stay.checkIn)}</b> {stay.dropOff === 'pm' ? 'afternoon' : 'morning'}
        </span>
        <em>{nights}</em>
        <span>
          {G.sunset({ size: 13 })}
          <b>{longDay(stay.checkOut)}</b> {stay.collection === 'am' ? 'morning' : 'afternoon'}
        </span>
      </div>
      {/* ONE STRUCTURE IN EVERY PHASE (Freddi, 2026-10-09): the strip stays on
          the staying and collected cards, all four stages done — the card is
          the same shape from request to collection, the pill the only difference. */}
      {(
        <div className="cp-pg">
          {stageStrip(stay.status).map((s, i) => {
            const st = STAGES[i]!;
            return (
              <span key={st.key} className={`pg-s ${s} s-${st.key}`}>
                <i>
                  {st.icon({ size: 13 })}
                  {s === 'done' && <b className="pg-b">{G.tick({ size: 8, strokeWidth: 3.4 })}</b>}
                  {s === 'now' && <b className="pg-b live" />}
                </i>
                <b>{st.word}</b>
              </span>
            );
          })}
        </div>
      )}
      {rows.length > 0 && (
        <dl className="cp-kv">
          {rows.map((r) => (
            <div key={r.label} className={'kv-row' + (r.cls ? ' ' + r.cls : '')}>
              <dt>
                {r.label}
                {r.tag === 'if accepted' && <small className="kv-when">if accepted</small>}
                {r.tag && r.tag !== 'if accepted' && <Tag tone={r.tag === 'Due' ? 'due' : 'ok'}>{r.tag}</Tag>}
              </dt>
              <dd>{r.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className={'hs-allset' + (say.ico === 'tick' ? '' : ' hs-need')}>
        {SAY_ICON[say.ico]({ size: 15 })}
        <span>
          <b>{say.lead}</b> {say.rest}
        </span>
      </p>
      {door && door.kind === 'message' && (
        <PendingButton className="cp-btn ghost hs-pay hs-pay-quiet" icon={<WhatsAppMark size={15} />} href={WHATSAPP_HREF} external>
          {door.label}
        </PendingButton>
      )}
      {/* THE MONEY DOORS OPEN THE PAY SHEET (deposit-proof AC1, AC19): Transfer
          and View go to `/pay/<id>` over Home; WhatsApp is the fallback only
          where nothing can navigate, as it was before the sheet existed. */}
      {door && door.kind !== 'message' && (
        onGo ? (
          <PendingButton className={door.primary ? 'cp-btn primary hs-pay' : 'cp-btn ghost hs-pay hs-pay-quiet'} icon={G.camera({ size: 15 })} disabled={door.disabled} onClick={() => onGo(payPath(stay.id))}>
            {door.label}
          </PendingButton>
        ) : (
          <PendingButton className={door.primary ? 'cp-btn primary hs-pay' : 'cp-btn ghost hs-pay hs-pay-quiet'} icon={G.euro({ size: 15 })} href={whatsappHref(opener(door.kind, { contactName, dogs: on.names, stay }))} external>
            {door.label}
          </PendingButton>
        )
      )}
    </div>
  );
}
